// Sprint 11b.1 — Active workspace (project) persistence.
//
// Zustand persist store keyed by 'asoasis-active-workspace-id'. Drives
// WorkspaceColumn highlighting and sidebar agent filtering.
//
// Self-healing:
//   WS10 — if persisted value is 'default' (pre-migration), rewrite to 'cw-crm-v2'.
//   WS3  — on every projects refetch, if activeWorkspaceId is missing from
//          the non-archived list, fall back to first alphabetical.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface ActiveWorkspaceState {
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string) => void;
}

export const useActiveWorkspaceStore = create<ActiveWorkspaceState>()(
  persist(
    (set) => ({
      activeWorkspaceId: null,

      setActiveWorkspaceId: (id: string) => {
        set({ activeWorkspaceId: id });
      },
    }),
    {
      name: 'asoasis-active-workspace-id',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.activeWorkspaceId === 'default') {
          state.setActiveWorkspaceId('cw-crm-v2');
        }
      },
    },
  ),
);

/**
 * WS3 + WS10 — call after every projects list fetch to ensure the active
 * workspace still exists and is not archived. Falls back to the first
 * non-archived project sorted alphabetically.
 */
export function healActiveWorkspace(projectIds: string[]): void {
  const { activeWorkspaceId, setActiveWorkspaceId } =
    useActiveWorkspaceStore.getState();

  if (activeWorkspaceId === 'default' && !projectIds.includes('default')) {
    if (projectIds.includes('cw-crm-v2')) {
      setActiveWorkspaceId('cw-crm-v2');
      return;
    }
  }

  if (!activeWorkspaceId || !projectIds.includes(activeWorkspaceId)) {
    const sorted = [...projectIds].sort();
    if (sorted.length > 0 && sorted[0] !== undefined) {
      setActiveWorkspaceId(sorted[0]);
    }
  }
}
